import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

export type UserVariables = { accountId: string };

export type User = {
	accountId: string;
	id: string;
	name: string;
	picture: string;
};

export type UserResponse = {
	user: User;
};

export const cache: { clear: (key?: string) => void; get: (key: string) => Promise<AggResponse<UserResponse>> | undefined; has: (key: string) => boolean; inMemoryDecorator: (key: string, supplier: () => Promise<AggResponse<UserResponse>>, expireWhen?: ((result: AggResponse<UserResponse>) => boolean) | undefined) => Promise<AggResponse<UserResponse>>; set: (key: string, result: Promise<AggResponse<UserResponse>>) => void; } = inMemoryCache<AggResponse<UserResponse>>();

const GQL_OPERATION_NAME = 'SearchPageUserQuery';
const getUserQuery = () => `
query ${GQL_OPERATION_NAME}($accountId: ID!) {
  user(accountId: $accountId) {
    id
    accountId
    name
    picture
  }
}
`;

export const userQuery = (accountId: string): Promise<AggResponse<UserResponse>> => {
	return cache.inMemoryDecorator(accountId, async () => {
		const response = await fetchAgg<UserVariables, UserResponse>({
			graphQuery: getUserQuery(),
			variables: { accountId },
			operationName: GQL_OPERATION_NAME,
		});

		const hasErrors = response.errors?.some((error) => error);

		if (hasErrors) {
			return Promise.reject(response);
		}
		return response;
	});
};

export type UsersQueryVariables = { accountIds: string[] };

export const usersQueryCache: { clear: (key?: string) => void; get: (key: string) => Promise<AggResponse<UsersResponse>> | undefined; has: (key: string) => boolean; inMemoryDecorator: (key: string, supplier: () => Promise<AggResponse<UsersResponse>>, expireWhen?: ((result: AggResponse<UsersResponse>) => boolean) | undefined) => Promise<AggResponse<UsersResponse>>; set: (key: string, result: Promise<AggResponse<UsersResponse>>) => void; } = inMemoryCache<AggResponse<UsersResponse>>();

const GQL_USERS_OPERATION_NAME = 'SearchPageUsersQuery';
const getUsersQuery = () => `
query ${GQL_USERS_OPERATION_NAME}($accountIds: [ID!]!) {
  users(accountIds: $accountIds) {
    id
    name
    picture
    accountId
  }
}
`;

export type UsersResponse = {
	users: User[];
};

export const usersQuery = (accountIds: string[]): Promise<AggResponse<UsersResponse>> => {
	return usersQueryCache.inMemoryDecorator(
		// TODO: >>>> Limit this to a max string length
		// TODO: Refactor so that it can cache per user id
		accountIds.join(','),
		async () => {
			const response = await fetchAgg<UsersQueryVariables, UsersResponse>({
				graphQuery: getUsersQuery(),
				variables: { accountIds },
				operationName: GQL_USERS_OPERATION_NAME,
			});

			const hasErrors = response.errors?.some((error) => error);

			if (hasErrors) {
				return Promise.reject(response);
			}
			return response;
		},
	);
};
